import { AskConfirmation, buttonsYesNo } from '../../../../webmodule-common/other/ui/modal-confirmation';
import { constructAsync } from '../../../../webmodule-common/other/async-constructor';
import { customElement, state } from 'lit/decorators.js';
import { DataBinding } from '../../../../webmodule-common/other/ui/databinding/databinding';
import { DataEntryOwner } from '../../../../webmodule-common/other/ui/DataEntryOwner';
import { DataEntryTabManagerView, ModalViewBase } from '../../../../webmodule-common/other/ui/data-entry-screen-base';
import { DataTracker, FieldType } from '../../../../webmodule-common/other/ui/databinding/data-tracker';
import {
  FabricationPurchaseOrderState,
  PurchaseOrderItemSource,
  ResultPurchaseOrder,
  SSEFabricationPurchaseOrder
} from '../../../api/fabrication-purchase-order-api';
import { FabricationPurchaseOrderItemsTable } from './fabrication-purchase-order-items-table';
import { FabricationPurchaseOrderLinkedJobsTable, LinkedJobRow } from './fabrication-purchase-order-linked-jobs-table';
import { fireQuickErrorToast, fireQuickSuccessToast } from '../../../../webmodule-common/other/toast-away';
import { FormInputAssistant } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { interpolateExternal } from '../../../../localization/global';
import { getApiFactory } from '../../../api/api-injector';
import { canIssue, dispatchIssue } from '../data/issue-action';
import { moneyToStr, stringToCurrencyCode } from '../../../../webmodule-common/other/currency-formatter';
import {
  canCancelPurchaseOrder,
  canCompletePurchaseOrder,
  getPurchaseOrderNumberFormatted,
  getPurchaseOrderStatusBadge
} from '../data/fabrication-purchase-order-helper-functions';
import { html, TemplateResult } from 'lit';
import { LangFabricationPurchaseOrder } from '../../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';
import { TabManager } from '../../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type { TabAction, TabPage } from '../../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { Snippet, NullPromise } from '../../../../webmodule-common/other/ui/events';
import { saveWithIndicator } from '../../../../webmodule-common/other/save-workflow';
import { WMEventSource } from '../../../api/event-source';
import {
  addComponentLine,
  addExtrusionLine,
  addFillLine,
  addGasketLine,
  AdHocLineContext
} from './pickers/ad-hoc-line-orchestrators';
import { adHocPickersSupported } from './pickers/ad-hoc-pickers-support';
import { cache } from '../../cache-impl/cache-registry';
import { AdHocLineSelection, submitAdHocLine } from '../data/ad-hoc-line-input';

export interface FabricationPurchaseOrderViewOptions {
  result: ResultPurchaseOrder;
  onClose: () => void;
  isLockOwner?: () => boolean;
}

@customElement('wm-fabricationpurchaseorderdetailview')
export class FabricationPurchaseOrderDataEntryView extends DataEntryTabManagerView {
  private options: FabricationPurchaseOrderViewOptions;
  private result: ResultPurchaseOrder;
  private dataBinding: DataBinding;
  private dataTracker: DataTracker;
  @state()
  private itemsTable?: FabricationPurchaseOrderItemsTable;
  @state()
  private linkedJobsTable?: FabricationPurchaseOrderLinkedJobsTable;
  private linkedJobs: LinkedJobRow[] = [];

  // The last-saved baseline. Dirty detection compares the live object to this
  // (not dataTracker.modified, which prepareForSave clears before the save flow
  // checks it - see the job detail view's containerManager.jobChanged() pattern).
  private baseline = {
    title: '',
    description: '',
    quantities: new Map<string, number>(),
    quantityDisplays: new Map<string, string | null>()
  };

  constructor(options: FabricationPurchaseOrderViewOptions, owner: DataEntryOwner) {
    super(owner);
    this.options = options;
    this.result = options.result;

    this.dataBinding = new DataBinding(this.ui, undefined, input => `fabrication-purchase-order-${input}`);
    this.dataTracker = new DataTracker(this.dataBinding);

    const addField = (fieldName: string, type: FieldType = FieldType.string, nullable = false) => {
      this.dataTracker.addObjectBinding(() => this.header, fieldName, fieldName, type, nullable);
    };

    addField('title');
    addField('description');

    // Supplier-status fields, rendered read-only the same way as the standard dealer PO.
    addField('reference');
    addField('supplierSystemDeliveryDate', FieldType.date, true);
    addField('supplierSystemClosedDate', FieldType.date, true);
    addField('supplierSystemShippingLeadTime', FieldType.int, true);
    // Status is a code from the supplier's V6; show the friendly localized text (matches the standard PO).
    this.dataTracker.addDynamic(
      'supplierSystemStatus',
      FieldType.string,
      () => (this.header.supplierSystemStatus ? interpolateExternal(this.header.supplierSystemStatus) : ''),
      undefined,
      { nullable: true }
    );
    // Author is read-only on purchase orders by design (TaskUpdatePurchaseOrderHeader does not
    // persist AssignedToUserId), so show the name rather than binding a user picker.
    this.dataTracker.addDynamic(
      'assignedToUserId',
      FieldType.string,
      () => cache().userProfile.getLocal(this.header.assignedToUserId)?.displayValue ?? ''
    );

    this.snapshotBaseline();
  }

  // Capture the current header + line quantities as the saved baseline. Called on
  // load and after every successful persist, so internalDataChanged() reflects only
  // edits made since the last save.
  private snapshotBaseline(): void {
    this.baseline = {
      title: this.header.title,
      description: this.header.description,
      quantities: new Map(this.result.items.map(i => [i.id, i.quantity])),
      quantityDisplays: new Map(this.result.items.map(i => [i.id, i.quantityDisplay]))
    };
  }

  // A line is modified if its internal quantity changed, or if only the formatted
  // length text changed (re-typing a length that parses to the same internal value
  // but a different display string, e.g. "1 m" -> "1000mm"). Tracking the display too
  // keeps the dirty state honest: the save re-baselines the new (V6-canonical) text
  // so it survives instead of reverting on the next rebuild from result.
  private lineQuantitiesModified(): boolean {
    return this.result.items.some(
      i =>
        this.baseline.quantities.get(i.id) !== i.quantity ||
        this.baseline.quantityDisplays.get(i.id) !== i.quantityDisplay
    );
  }

  private get header() {
    return this.result.purchaseOrder;
  }

  public async afterConstruction(): Promise<void> {
    await super.afterConstruction();
    WMEventSource.getInstance().addEventListener(WMEventSource.fabricationPurchaseOrder, this._eventPurchaseOrder);
    await cache().userProfile.preFetch([this.header.assignedToUserId]);
  }

  async dispose(): Promise<void> {
    WMEventSource.getInstance().removeEventListener(WMEventSource.fabricationPurchaseOrder, this._eventPurchaseOrder);
    await super.dispose();
  }

  // Why state the IssuedPending exit separately when the read-only clause below happens to
  // cover it as well? Because that clause is about edit rights, not about the issue flow -
  // this is the one transition the page cannot learn any other way (it completes in the
  // BackOffice), so it does not ride on a coincidence. Mirrors quote-view.ts for a quote
  // leaving IssuePending.
  private _eventPurchaseOrder = (_data: unknown, _eventName: string) => {
    const data = _data as SSEFabricationPurchaseOrder;
    if (data.id !== this.header.id) return;
    const resourceUpdateRequired =
      (this.header.state === FabricationPurchaseOrderState.IssuedPending &&
        data.state !== FabricationPurchaseOrderState.IssuedPending) ||
      (data.recordVersion !== this.header.recordVersion && this.isDataReadonly());
    if (resourceUpdateRequired)
      this.ui.dispatchEvent(
        new CustomEvent('resource-changed', {
          bubbles: true,
          composed: true,
          detail: {}
        })
      );
  };

  getTitle(): Snippet {
    return html`${getPurchaseOrderNumberFormatted(this.header)} ${getPurchaseOrderStatusBadge(this.header.state)}`;
  }

  private isPoReadonly(): boolean {
    return this.header.state !== FabricationPurchaseOrderState.Draft;
  }

  // Supplier pricing (per-line + total) is only populated once the PO is issued.
  private isPriced(): boolean {
    return (
      this.header.state === FabricationPurchaseOrderState.Issued ||
      this.header.state === FabricationPurchaseOrderState.Completed
    );
  }

  // The recordVersion the concurrency check compares against the server's, used to
  // detect a stale view on lock recovery (disconnect/wake).
  public get currentRecordVersion(): string {
    return this.header.recordVersion;
  }

  // The ordered line set is frozen once the PO carries a quote common id: V6 may already hold a
  // supplier quote keyed to it, and re-issuing returns that quote rather than quoting the edited
  // lines (see FabricationExtensions.CheckLineSetEditable). Deliberately NOT folded into
  // isDataReadonly() - that would strip the whole button band, including Issue, and strand a
  // reverted PO with no way to retry.
  private isLineSetLocked(): boolean {
    return this.header.quoteCommonId != null;
  }

  public isDataReadonly(): boolean {
    // Read-only when the PO is not Draft, or when this user does not hold the edit
    // lock (a second viewer can open the PO but only the lock owner may edit it).
    return this.isPoReadonly() || !this.isLockOwner();
  }

  private isLockOwner(): boolean {
    return this.options.isLockOwner?.() ?? true;
  }

  public internalDataChanged(): boolean {
    return (
      !this.abandoned &&
      (this.header.title !== this.baseline.title ||
        this.header.description !== this.baseline.description ||
        this.lineQuantitiesModified())
    );
  }

  public async prepareForSave(): Promise<void> {
    if (this.isDataReadonly()) return;
    // Copy the header form inputs onto the PO object so internalDataChanged()/save see them.
    if (this.dataTracker.modified) this.dataTracker.applyChangeToValue();
  }

  protected async internalSaveData(): Promise<boolean> {
    const api = getApiFactory().fabricationPurchaseOrder();

    const headerChanged =
      this.header.title !== this.baseline.title || this.header.description !== this.baseline.description;
    // Capture pending quantities up front: the header save below reassigns this.result
    // to the server PO (line quantities at their saved values), which would otherwise
    // clobber the in-memory quantity edits before we get to persist them.
    const pendingQuantities = this.result.items
      .filter(i => this.baseline.quantities.get(i.id) !== i.quantity)
      .map(i => ({ itemId: i.id, quantity: i.quantity }));

    if (headerChanged) {
      const result = await api.updatePurchaseOrderHeader({
        id: this.header.id,
        recordVersion: this.header.recordVersion,
        title: this.header.title,
        description: this.header.description
      });
      if (!result) return false;
      this.result = result;
    }

    for (const pending of pendingQuantities) {
      const result = await api.updatePurchaseOrderLineQuantity({
        purchaseOrderId: this.header.id,
        recordVersion: this.header.recordVersion,
        itemId: pending.itemId,
        quantity: pending.quantity
      });
      if (!result) return false;
      this.result = result;
    }

    this.snapshotBaseline();
    if (this.itemsTable) await this.itemsTable.refreshData();
    return true;
  }

  protected createTabManager(): TabManager {
    return new TabManager({
      initialIndex: 0,
      actions: this.buildTabActions(),
      pages: () => [this.createInformationTab(), this.createItemsTab(), this.createLinkedJobsTab()]
    });
  }

  // Why a method rather than an inline array? Because TabManager captures the action list once at
  // construction, so a state change has to rebuild and re-apply it (see applyResult) - otherwise
  // the Draft-only buttons stay on the bar after issuing and silently do nothing when clicked.
  // Why is this list in reverse reading order? TabManager keeps the legacy band's contract -
  // the first action is the right-most one - by reversing the array before it renders
  // (overallRegionTemplate). So Status leads the list to sit right-most, and the Add buttons
  // follow in reverse to read extrusion -> fill left-to-right.
  private buildTabActions(): TabAction[] {
    const actions: TabAction[] = [];
    const status = this.statusTabAction();
    if (status) actions.push(status);
    // The line set stays frozen on a line-set-locked PO even though Issue (the retry after a
    // failed issue) is still offered from the Status menu.
    if (this.isDataReadonly() || this.isLineSetLocked()) return actions;
    actions.push(
      this.addLineTabAction(() => LangFabricationPurchaseOrder.addFill(), addFillLine),
      this.addLineTabAction(() => LangFabricationPurchaseOrder.addGasket(), addGasketLine),
      this.addLineTabAction(() => LangFabricationPurchaseOrder.addComponent(), addComponentLine),
      this.addLineTabAction(() => LangFabricationPurchaseOrder.addExtrusion(), addExtrusionLine)
    );
    return actions;
  }

  // The Status drop-up, matching the quote view: a single TabAction carrying child items renders
  // as a drop-up. Why gate on the lock rather than isDataReadonly()? Because an Issued PO is
  // read-only yet still has to be completed or cancelled - only a viewer without the lock is shut
  // out. Returns null when no transition is legal (IssuedPending, Completed, Cancelled), so the
  // button is absent rather than empty.
  private statusTabAction(): TabAction | null {
    if (!this.isLockOwner()) return null;
    const state = this.header.state;
    const items: TabAction[] = [];
    if (state === FabricationPurchaseOrderState.Draft)
      items.push({
        caption: () => LangFabricationPurchaseOrder.issueAction(),
        onClick: async () => await this.issuePurchaseOrder()
      });
    if (canCompletePurchaseOrder(state))
      items.push({
        caption: () => LangFabricationPurchaseOrder.completeAction(),
        onClick: async () => await this.completePurchaseOrder()
      });
    if (canCancelPurchaseOrder(state))
      items.push({
        caption: () => LangFabricationPurchaseOrder.cancelAction(),
        onClick: async () => await this.cancelPurchaseOrder()
      });
    if (items.length === 0) return null;
    return { caption: () => LangFabricationPurchaseOrder.statusButton(), items };
  }

  private async completePurchaseOrder(): Promise<void> {
    if (
      !(await AskConfirmation(
        LangFabricationPurchaseOrder.completeConfirm(this.header.purchaseOrderNumber),
        buttonsYesNo()
      ))
    )
      return;
    const api = getApiFactory().fabricationPurchaseOrder();
    if (await this.persistAndApply(() => api.completePurchaseOrder(this.versionedId())))
      fireQuickSuccessToast(LangFabricationPurchaseOrder.completedToast());
  }

  private async cancelPurchaseOrder(): Promise<void> {
    // An Issued order has reached the supplier, and so has a Draft order carrying a quote common
    // id: an issue that failed after the V6 quote was created reverts to Draft but keeps the key.
    // Cancelling is dealer-side only, so the supplier is left holding an order nobody has
    // withdrawn - say so rather than reusing the plain "cannot be undone" wording.
    const supplierMayHold =
      this.header.state === FabricationPurchaseOrderState.Issued || this.header.quoteCommonId != null;
    const message = supplierMayHold
      ? LangFabricationPurchaseOrder.cancelConfirmSupplierMayHold(this.header.purchaseOrderNumber)
      : LangFabricationPurchaseOrder.cancelConfirm();
    if (!(await AskConfirmation(message, buttonsYesNo()))) return;
    const api = getApiFactory().fabricationPurchaseOrder();
    if (await this.persistAndApply(() => api.cancelPurchaseOrder(this.versionedId())))
      fireQuickSuccessToast(LangFabricationPurchaseOrder.cancelledToast());
  }

  private versionedId(): { id: string; recordVersion: string } {
    return { id: this.header.id, recordVersion: this.header.recordVersion };
  }

  private createInformationTab(): TabPage {
    return {
      caption: () => LangFabricationPurchaseOrder.informationTab(),
      canLeave: async () => await this.allowPageSwitch(),
      content: () => this.informationTemplate(),
      fragment: 'information'
    };
  }

  private createItemsTab(): TabPage {
    return {
      caption: () => `${LangFabricationPurchaseOrder.itemsTab()} (${this.result.items.length})`,
      canLeave: async () => await this.allowPageSwitch(),
      onEnter: async () => {
        if (!this.itemsTable) {
          this.itemsTable = new FabricationPurchaseOrderItemsTable({
            items: () => this.result.items,
            isReadonly: () => this.isDataReadonly() || this.isLineSetLocked(),
            supplierId: this.header.supplierId,
            showPricing: () => this.isPriced(),
            currency: () => this.header.purchaseOrderCurrency,
            // Quantity edits defer like any other form field: update in memory + redraw
            // the pack/roll hint, then let the centralized save flow persist them (with
            // the save indicator) when the user leaves the tab/page. No API call here.
            onQuantityChange: async (item, qty) => {
              item.quantity = qty;
              if (this.itemsTable) await this.itemsTable.refreshData();
            },
            // Remove is an immediate discrete action (matches deleting a quote item), but
            // first flush any pending quantity/header edits so they are not clobbered by
            // the fresh PO snapshot the delete returns.
            onRemoveLine: async item => {
              if (!(await this.performAutoSave())) return;
              await this.persistAndApply(() =>
                getApiFactory().fabricationPurchaseOrder().removePurchaseOrderLine({
                  purchaseOrderId: this.header.id,
                  recordVersion: this.header.recordVersion,
                  itemId: item.id
                })
              );
            }
          });
        } else {
          this.itemsTable.refreshData();
        }
      },
      content: () => html`${this.lineSetLockedTemplate()}${this.itemsTable}${this.orderTotalTemplate()}`,
      fragment: 'items'
    };
  }

  // Why explain only while the PO is otherwise editable? Because that is the baffling case -
  // a Draft PO the user may still issue, whose lines are nonetheless frozen. Past Draft the
  // whole PO is read-only and the state badge already says so.
  private lineSetLockedTemplate(): TemplateResult {
    if (this.isDataReadonly() || !this.isLineSetLocked()) return html``;
    return html`<div class="alert alert-info" role="alert">${LangFabricationPurchaseOrder.lineSetLocked()}</div>`;
  }

  // Supplier order total, shown under the items grid once the PO is issued (zero/empty before then).
  private orderTotalTemplate(): TemplateResult {
    if (!this.isPriced()) return html``;
    return html`
      <div class="d-flex justify-content-end mt-2 fabrication-purchase-order-total">
        <span class="me-2 text-muted">${LangFabricationPurchaseOrder.totalLabel()}</span>
        <span class="fw-bold"
          >${moneyToStr(this.header.calculatedNetTotal, stringToCurrencyCode(this.header.purchaseOrderCurrency))}</span
        >
      </div>
    `;
  }

  // One "Add <type>" action per material type, placed on the tab button bar (only present
  // while the PO is editable - the whole action set is Draft-only).
  // Each runs its picker orchestrator then applies the returned PO via addLine.
  private addLineTabAction(
    caption: () => string,
    orchestrator: (ctx: AdHocLineContext) => NullPromise<AdHocLineSelection>
  ): TabAction {
    return {
      caption,
      onClick: async () => {
        await this.addLine(orchestrator);
      }
    };
  }

  // Flush any pending quantity/header edits (so the fresh PO snapshot the add returns does
  // not clobber them), then run the interactive orchestrator to collect the user's selection.
  // The picking (resource family -> finish -> length / measurement dialogs) must run OUTSIDE
  // saveWithIndicator - otherwise the background locks and a "saving" indicator shows the whole
  // time the user is still choosing. Only the final submit + refresh is wrapped in the
  // indicator, mirroring the quote-item add flow (pick first, then save) and onRemoveLine.
  private async addLine(orchestrator: (ctx: AdHocLineContext) => NullPromise<AdHocLineSelection>): Promise<void> {
    if (this.isDataReadonly()) return;
    if (!(await this.performAutoSave())) return;
    // Gate every orchestrator once here rather than repeating the check in each: a supplier whose V6
    // is too old to build/optimize/issue an ad-hoc PO must not reach any picker. adHocPickersSupported
    // toasts when unsupported.
    if (!adHocPickersSupported(this.header.supplierId)) return;
    const selection = await orchestrator({
      purchaseOrderId: this.header.id,
      recordVersion: this.header.recordVersion,
      supplierId: this.header.supplierId
    });
    if (!selection) return;
    await this.persistAndApply(() => submitAdHocLine(selection));
  }

  // Run a mutating PO call under the save indicator and, on success, apply the fresh PO via the
  // existing refresh path. Shared by the add-line and remove-line flows.
  private async persistAndApply(call: () => NullPromise<ResultPurchaseOrder>): Promise<boolean> {
    return await saveWithIndicator(async () => {
      const result = await call();
      if (!result) return false;
      await this.applyResult(result);
      return true;
    });
  }

  private createLinkedJobsTab(): TabPage {
    return {
      caption: () => `${LangFabricationPurchaseOrder.linkedJobsTab()} (${this.result.linkedJobIds.length})`,
      canLeave: async () => await this.allowPageSwitch(),
      onEnter: async () => {
        if (!this.linkedJobsTable) {
          this.linkedJobsTable = new FabricationPurchaseOrderLinkedJobsTable({
            items: () => this.linkedJobs
          });
        }
        // A loaded PO's linked-job set is fixed, so fetch once; a count mismatch also
        // retries any job whose fetch failed last time.
        if (this.linkedJobs.length !== this.result.linkedJobIds.length) {
          await this.loadLinkedJobs();
        }
        await this.linkedJobsTable.refreshData();
      },
      content: () => html`${this.linkedJobsTable}`,
      fragment: 'linked-jobs'
    };
  }

  // Fetch each linked job (by id, in parallel) and reduce it to the number/title the
  // grid shows. Failed fetches are dropped rather than blocking the rest.
  private async loadLinkedJobs(): Promise<void> {
    const api = getApiFactory().fabricationJob();
    const results = await Promise.all(this.result.linkedJobIds.map(id => api.getFabricationJob({ id })));
    this.linkedJobs = results.flatMap(r =>
      r ? [{ id: r.fabricationJob.id, jobNumber: r.fabricationJob.jobNumber, title: r.fabricationJob.title }] : []
    );
  }

  // Re-assign result after a mutating call so the next call sees the fresh
  // recordVersion, re-baseline (the server state is now the saved truth), then
  // refresh the items table, re-evaluate the band buttons against the new state
  // (issuing drops them all) and redraw the tab captions.
  private async applyResult(result: ResultPurchaseOrder): Promise<void> {
    this.result = result;
    this.snapshotBaseline();
    if (this.itemsTable) await this.itemsTable.refreshData();
    // Guard the setActions call through the backing field: the tabManager getter throws
    // rather than returning null when the control has not been created yet.
    this._tabManager?.setActions(this.buildTabActions());
    this.requestUpdate();
    this.refreshParent();
  }

  // Issue a Draft PO to the supplier. The pure eligibility check (canIssue) and the dispatch
  // (dispatchIssue) live in issue-action so they are unit-testable; this method supplies the live
  // view's state and side-effects.
  // Why flush pending edits first, like addLine? Because the supplier is quoted the STORED
  // quantities - an unsaved edit would be ordered at its old value and then wiped by the reload,
  // and the PO is read-only from IssuedPending on, so it could never be re-applied. Flushing also
  // has to precede the guard counts below: internalSaveData reassigns this.result.
  private async issuePurchaseOrder(): Promise<void> {
    if (this.isDataReadonly()) return;
    if (!(await this.performAutoSave())) return;

    // Externally-sourced lines are not ordered from the supplier (the BackOffice ad-hoc line
    // builder filters them out), so the issue guard needs the supplier-sourced count too.
    const orderableItemCount = this.result.items.filter(i => i.source !== PurchaseOrderItemSource.External).length;
    switch (canIssue(this.header.state, this.result.items.length, orderableItemCount)) {
      case 'not-draft':
        return;
      case 'no-items':
        fireQuickErrorToast(LangFabricationPurchaseOrder.issueNoItems());
        return;
      case 'no-orderable-items':
        fireQuickErrorToast(LangFabricationPurchaseOrder.issueNoOrderableItems());
        return;
    }

    // Why confirm before saveWithIndicator, not inside it? saveWithIndicator disables the UI and
    // raises the "saving" backdrop; a confirmation opened inside it would sit over a greyed, busy
    // UI. Pickers and onRemoveLine confirm first for exactly this reason.
    if (!(await AskConfirmation(LangFabricationPurchaseOrder.issueConfirm(), buttonsYesNo()))) {
      return;
    }

    await saveWithIndicator(async () => {
      return await dispatchIssue({
        purchaseOrderId: this.header.id,
        recordVersion: this.header.recordVersion,
        issue: input => getApiFactory().fabricationPurchaseOrder().issuePurchaseOrder(input),
        reload: () => this.reloadHeader(),
        onIssued: () => fireQuickSuccessToast(LangFabricationPurchaseOrder.issuedPendingToast())
      });
    });
  }

  // The issue endpoint returns ResultVoid (the PO is now IssuedPending server-side), so re-fetch
  // the full PO to reflect the new state. Why not wait for the SSE the same transaction pushes?
  // Because the user needs immediate confirmation that the dispatch was accepted; the event is
  // what carries the later Issued/Draft outcome.
  private async reloadHeader(): Promise<void> {
    const reloaded = await getApiFactory().fabricationPurchaseOrder().getPurchaseOrder({ id: this.header.id });
    if (reloaded) await this.applyResult(reloaded);
  }

  private informationTemplate(): TemplateResult {
    const readonly = this.isDataReadonly();
    const forms = new FormInputAssistant(this.dataTracker, readonly);

    return html`
      <form class="py-3 px-0 form-two-col">
        <div class="row">
          <div>
            ${forms.text('title', LangFabricationPurchaseOrder.titleLabel(), 200)}
            ${forms.note('description', LangFabricationPurchaseOrder.descriptionLabel(), 3000)}
            ${forms.textReadonly('assignedToUserId', LangFabricationPurchaseOrder.authorLabel())}
          </div>
        </div>
      </form>
      ${this.supplierStatusTemplate()}
    `;
  }

  // Read-only supplier-status section, shown only once the PO is Issued or Completed (the fields
  // are empty/zero while Draft or IssuedPending). Reflects whatever the last load/refresh fetched;
  // the supplier-status poller updates these server-side and they appear on the next reload.
  private supplierStatusTemplate(): TemplateResult {
    if (!this.isPriced()) return html``;
    // Read-only supplier fields, using the same FormInputAssistant components the standard
    // dealer PO detail view uses so the block renders identically.
    const forms = new FormInputAssistant(this.dataTracker, true);
    return html`
      <div class="row mt-3 fabrication-purchase-order-supplier-status">
        <div>
          <h6>${LangFabricationPurchaseOrder.supplierStatusHeading()}</h6>
          ${forms.textReadonly('reference', LangFabricationPurchaseOrder.referenceLabel())}
          ${forms.textReadonly('supplierSystemStatus', LangFabricationPurchaseOrder.supplierStatusLabel())}
          ${forms.dateReadonly('supplierSystemDeliveryDate', LangFabricationPurchaseOrder.deliveryDateLabel(), 'date')}
          ${forms.dateReadonly('supplierSystemClosedDate', LangFabricationPurchaseOrder.closedDateLabel(), 'date')}
          ${forms.intReadonly('supplierSystemShippingLeadTime', LangFabricationPurchaseOrder.leadTimeLabel())}
        </div>
      </div>
    `;
  }
}

@customElement('wm-fabricationpurchaseorderview')
export class FabricationPurchaseOrderView extends ModalViewBase {
  view: FabricationPurchaseOrderDataEntryView | null = null;
  private options: FabricationPurchaseOrderViewOptions;

  constructor(options: FabricationPurchaseOrderViewOptions) {
    super();
    this.options = options;
  }

  protected get modalSize() {
    return 'modal-fullscreen';
  }

  async canClose(): Promise<boolean> {
    return (await this.view?.canClose()) ?? true;
  }

  async afterConstruction(): Promise<void> {
    this.view = await constructAsync(new FabricationPurchaseOrderDataEntryView(this.options, this));
  }

  protected getTitle(): Snippet {
    return this.view?.getTitle() ?? '';
  }

  protected bodyTemplate(): TemplateResult {
    return html`${this.view?.ui}`;
  }
}
